import { trackEvent } from "../utils/track-event";
import type { TrackingEvent, TrackingEventResponse } from "../utils/types";
import type {
  CatalogProduct,
  HomepageSingleCategoryProduct,
} from "src/types/graphql.d";
import type { SEGMENT_LIST_FORMAT, SegmentProductListMeta } from "src/segment";

type ProductClickedProduct = CatalogProduct | HomepageSingleCategoryProduct;

type ProductClickedEventProps = {
  product: ProductClickedProduct;
  position: number;
  productListMeta: SegmentProductListMeta;
  resultId?: string;
  query?: string;
};

type ProductClickedAnalyticsProps = {
  brand: string;
  brand_id: string;
  category_id: unknown;
  category: unknown;
  image_url: string;
  labels: string[];
  list_format: SEGMENT_LIST_FORMAT;
  list_id: string;
  list_type: string;
  name: string;
  path: string;
  position: number;
  price: number;
  product_discount: number;
  product_id: string;
  product_kids_gender?: unknown;
  product_shop_type?: unknown;
  result_id?: string;
  regular_price: number;
  shop_type?: string;
  sizes_available: unknown;
  url: string;
  gate_level: unknown;
  query?: string;
};

export type ProductClickedEvent = TrackingEvent<
  "Product Clicked",
  ProductClickedAnalyticsProps
>;

export async function trackProductClickedEvent({
  product,
  position,
  productListMeta: { listFormat, listId, listType, shopType },
  query,
  resultId,
}: ProductClickedEventProps): TrackingEventResponse {
  const {
    price: catalogProductPrice,
    regular_price,
    available_sizes,
  } = product as CatalogProduct;

  const { price_float, regular_price_float, sizes_available } =
    product as HomepageSingleCategoryProduct;

  const price = catalogProductPrice || price_float;
  const regularPrice = regular_price || regular_price_float;
  const sizesAvailable = available_sizes || sizes_available || [];

  return await trackEvent("Product Clicked", {
    props: {
      brand: product.brand.name,
      brand_id: product.brand.id,
      category_id: product.translated_category_ids,
      category: product.translated_category_names,
      image_url: product.thumbnail,
      labels: (product.labels || []).map(({ value }) => value),
      list_format: listFormat,
      list_id: listId,
      list_type: listType,
      name: product.name,
      path: typeof window !== "undefined" ? window.location.pathname : "",
      position,
      price,
      product_discount:
        product?.product_discount ||
        Math.round((regularPrice - price + Number.EPSILON) * 100) / 100,
      product_id: product.id,
      product_kids_gender: product.product_shop_type?.includes("kids")
        ? product.product_kids_gender
        : undefined,
      product_shop_type: product.product_shop_type,
      regular_price: regularPrice,
      shop_type: shopType,
      sizes_available: sizesAvailable,
      url: `/product/${product.slug}`,
      gate_level: product.gate?.level || null,
      query,
      result_id: resultId,
    },
    withRequestSource: true,
  });
}
